import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { ArrowLeft } from "lucide-react";
import SmsConnectionCard from "@/components/sms/SmsConnectionCard";
import SmsReminderSettings from "@/components/sms/SmsReminderSettings";
import SmsTestCard from "@/components/sms/SmsTestCard";
import SmsHistory from "@/components/sms/SmsHistory";
import SmsReminderQueue from "@/components/sms/SmsReminderQueue";
import SmsGoLiveChecklist from "@/components/sms/SmsGoLiveChecklist";
import SmsFailedList from "@/components/sms/SmsFailedList";
import { useOutletContext } from "react-router-dom";
import SmsChangeTemplates from "@/components/sms/SmsChangeTemplates";
import SmsCleanerDigest from "@/components/sms/SmsCleanerDigest";

export default function SmsNotifications() {
  const [overview, setOverview] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);
  const { refreshAlerts } = useOutletContext() || {};

  const load = useCallback(() => {
    api.get("/sms/overview").then((r) => setOverview(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const afterSend = () => { setHistoryKey((k) => k + 1); load(); };

  return (
    <div data-testid="sms-notifications-page">
      <Link to="/settings" data-testid="sms-back-to-settings" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-brand transition-colors mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Settings
      </Link>
      <PageHeader
        eyebrow="Settings · Notifications"
        title="SMS Notifications"
        description="Automatic Twilio text reminders for upcoming bookings, sent in Australia/Brisbane time."
      />
      {!overview ? (
        <p className="text-sm text-muted-foreground" data-testid="sms-loading">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">
          <div className="xl:col-span-5 space-y-8">
            <SmsFailedList refreshKey={historyKey} onReviewed={() => refreshAlerts?.()} />
            {overview.go_live && <SmsGoLiveChecklist steps={overview.go_live} />}
          </div>
          <div className="xl:col-span-3 space-y-8">
            <SmsConnectionCard overview={overview} onChange={load} />
            <SmsReminderSettings overview={overview} onSaved={load} />
            <SmsChangeTemplates overview={overview} onSaved={load} />
          </div>
          <div className="xl:col-span-2 space-y-8 self-start">
            <SmsTestCard live={overview.sms_live} onSent={afterSend} />
            <SmsCleanerDigest overview={overview} onChange={afterSend} />
            {!overview.business_phone && (
              <div className="border border-brand/60 bg-brand/10 rounded-sm px-5 py-4 text-sm" data-testid="sms-business-phone-missing">
                Add your business phone in <Link to="/settings" className="text-brand underline">Settings → Business</Link> so <code>{"{business_phone}"}</code> shows in texts.
              </div>
            )}
          </div>
          <div className="xl:col-span-5 space-y-8">
            <SmsReminderQueue refreshKey={historyKey} onRan={afterSend} />
            <SmsHistory refreshKey={historyKey} />
          </div>
        </div>
      )}
    </div>
  );
}
